import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import type { Farmer } from '../../../../types';
import DetailOverviewTab from './DetailOverviewTab';
import DetailProductionTab from './DetailProductionTab';
import DetailDiaryTab from './DetailDiaryTab';
import DetailHarvestTab from './DetailHarvestTab';
import DetailEkycRiskTab from './DetailEkycRiskTab';
import DetailFinanceTab from './DetailFinanceTab';
import { getFarmerKyc, getDebtsByFarmer, getLimitsByFarmer, getLoansByFarmer } from '../../../../store';
import { formatCompactCurrency } from '../../../../utils/format';
import { useStore } from '../../../../hooks/useStore';
import { DEBT_STATUS, getCreditScoreColor } from '../../../../utils/status';

type SheetTabKey = 'overview' | 'production' | 'diary' | 'harvest' | 'ekycRisk' | 'finance';

const sheetTabs: { key: SheetTabKey; label: string }[] = [
  { key: 'overview', label: 'Tổng quan' },
  { key: 'production', label: 'Mùa vụ' },
  { key: 'diary', label: 'Nhật ký' },
  { key: 'harvest', label: 'Sản lượng' },
  { key: 'ekycRisk', label: 'eKYC & Risk' },
  { key: 'finance', label: 'Tài chính' },
];

interface FarmerDetailSheetProps {
  farmer: Farmer;
  open: boolean;
  onClose: () => void;
}

export default function FarmerDetailSheet({ farmer, open, onClose }: FarmerDetailSheetProps) {
  useStore();
  const [activeTab, setActiveTab] = useState<SheetTabKey>('overview');
  const backdropRef = useRef<HTMLDivElement>(null);

  const kyc = getFarmerKyc(farmer);
  const debts = getDebtsByFarmer(farmer.id);
  const limits = getLimitsByFarmer(farmer.id);
  const loans = getLoansByFarmer(farmer.id);

  const lendingSummary = useMemo(() => {
    const hanMucTong = limits.reduce((s, l) => s + l.hanMuc, 0);
    const daDung = limits.reduce((s, l) => s + l.daSuDung, 0);
    const conLai = hanMucTong - daDung;
    const dangKhoa = limits.reduce((s, l) => s + l.dangKhoa, 0);
    const totalNo = debts.filter((d) => d.conNo > 0).reduce((s, d) => s + d.conNo, 0);
    const noQuaHan = debts.filter((d) => d.status === DEBT_STATUS.OVERDUE).reduce((s, d) => s + d.conNo, 0);
    const usagePercent = hanMucTong > 0 ? (daDung / hanMucTong) * 100 : 0;
    // Locked ≠ owed: the lending flow reserves limit capacity the moment a cash
    // loan is submitted/approved, but it only becomes real debt once disbursed —
    // so "Còn lại" can be well under "Hạn mức" while "Dư nợ" still reads 0.
    const pendingLoanCount = loans.filter((l) => l.loai === 'Tiền' && ['Chờ kiểm tra', 'Cần bổ sung', 'Đã duyệt'].includes(l.status)).length;
    return { hanMucTong, daDung, conLai, dangKhoa, totalNo, noQuaHan, usagePercent, pendingLoanCount };
  }, [debts, limits, loans]);

  const isKycVerified = kyc.kycStatus === 'ekyc_verified';
  const scoreColor = getCreditScoreColor(kyc.creditScore);

  // Body scroll lock
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  // Esc key
  useEffect(() => {
    if (!open) return;
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [open, onClose]);

  const handleBackdropClick = useCallback((e: React.MouseEvent) => {
    if (e.target === backdropRef.current) onClose();
  }, [onClose]);

  // Reset tab when opening new farmer
  useEffect(() => {
    if (open) setActiveTab('overview');
  }, [farmer.id, open]);

  if (!open) return null;

  const lockReasons: string[] = [];
  // eKYC is not a lock any more (it only adds to the credit score) — the
  // overview tab carries the neutral reminder instead of a red banner here.
  if (lendingSummary.noQuaHan > 0) lockReasons.push(`Nợ quá hạn ${formatCompactCurrency(lendingSummary.noQuaHan)} — chặn duyệt vay mới`);

  return (
    <div className="fixed inset-0 z-[80] flex items-end lg:items-center justify-center">
      {/* Backdrop */}
      <div
        ref={backdropRef}
        className="absolute inset-0 bg-black/50 animate-modal-backdrop"
        onClick={handleBackdropClick}
      />

      {/* Sheet container */}
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative bg-white rounded-t-2xl lg:rounded-2xl shadow-xl w-full lg:max-w-[820px] flex flex-col animate-modal-slide-up"
        style={{ height: '95vh', maxHeight: '95vh' }}
      >
        {/* Header */}
        <div className="shrink-0 px-5 py-3 border-b border-slate-100">
          <div className="flex items-center gap-3">
            {/* Avatar */}
            <div className="w-10 h-10 rounded-full bg-agrida-100 flex items-center justify-center shrink-0">
              <span className="text-sm font-bold text-agrida-700">{farmer.name.charAt(0)}</span>
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="text-base font-bold text-slate-900 truncate">{farmer.name}</h2>
              <p className="text-xs text-slate-500 truncate">{[farmer.code, farmer.phone].filter(Boolean).join(' · ')}</p>
            </div>
            {/* Quick indicators */}
            <div className="flex items-center gap-1.5 shrink-0">
              {isKycVerified ? (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-agrida-100 text-agrida-700">eKYC</span>
              ) : (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-500">Chưa eKYC</span>
              )}
              {kyc.creditScore && (
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${scoreColor === 'text-agrida-600' ? 'bg-agrida-100 text-agrida-700' : scoreColor === 'text-amber-600' ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>
                  {kyc.creditScore}
                </span>
              )}
              <button
                type="button"
                onClick={onClose}
                className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-slate-100 text-slate-400 transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-xl" />
              </button>
            </div>
          </div>

          {/* Lock warnings */}
          {lockReasons.length > 0 && (
            <div className="mt-2 space-y-1">
              {lockReasons.map((r, idx) => (
                <div key={idx} className="flex items-start gap-1.5 text-xs text-red-700 bg-red-50 border border-red-100 rounded-xl px-2.5 py-1.5">
                  <span className="w-3.5 h-3.5 inline-flex items-center justify-center text-red-500 shrink-0 mt-px"><i className="ri-error-warning-fill" /></span>
                  <span>{r}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Quick stats bar */}
        <div className="shrink-0 px-5 py-2.5 border-b border-slate-50 bg-slate-50/50">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <div className="text-center">
              <p className="text-xs text-slate-400">Hạn mức</p>
              <p className="text-xs font-semibold text-slate-700 truncate">{lendingSummary.hanMucTong > 0 ? formatCompactCurrency(lendingSummary.hanMucTong) : '—'}</p>
            </div>
            <div className="text-center">
              <p className="text-xs text-slate-400">Còn lại</p>
              <p className={`text-xs font-semibold truncate ${lendingSummary.hanMucTong > 0 ? 'text-agrida-700' : 'text-slate-400'}`}>{lendingSummary.hanMucTong > 0 ? formatCompactCurrency(lendingSummary.conLai) : '—'}</p>
            </div>
            <div className="text-center">
              <p className="text-xs text-slate-400">Dư nợ</p>
              <p className={`text-xs font-semibold truncate ${lendingSummary.totalNo > 0 ? 'text-red-600' : 'text-slate-400'}`}>{lendingSummary.totalNo > 0 ? formatCompactCurrency(lendingSummary.totalNo) : '0'}</p>
            </div>
            <div className="text-center">
              <p className="text-xs text-slate-400">Quá hạn</p>
              <p className={`text-xs font-semibold truncate ${lendingSummary.noQuaHan > 0 ? 'text-red-600' : 'text-slate-400'}`}>{lendingSummary.noQuaHan > 0 ? formatCompactCurrency(lendingSummary.noQuaHan) : '0'}</p>
            </div>
          </div>
          {/* "Còn lại" can be lower than "Hạn mức − Dư nợ" while a cash loan is
              still pending/approved but not yet disbursed — the limit reserves
              that capacity before it ever becomes real debt. Without this note
              the gap reads as a data error. */}
          {lendingSummary.dangKhoa > 0 && (
            <p className="mt-2 flex items-start gap-1 text-[11px] text-amber-700">
              <i className="ri-lock-line mt-px shrink-0" />
              Đang khóa {formatCompactCurrency(lendingSummary.dangKhoa)} cho {lendingSummary.pendingLoanCount} khoản vay chưa giải ngân — chưa tính vào Dư nợ.
            </p>
          )}
        </div>

        {/* Tab bar */}
        <div className="shrink-0 px-5 py-2 border-b border-slate-100">
          <div className="flex gap-1 bg-slate-100 rounded-xl p-1 overflow-x-auto hide-scrollbar">
            {sheetTabs.map((tab) => {
              const isActive = activeTab === tab.key;
              return (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => setActiveTab(tab.key)}
                  className={`shrink-0 px-3 py-2 rounded-md text-xs font-medium text-center whitespace-nowrap transition-colors min-h-[36px] ${isActive ? 'bg-white text-agrida-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                >
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-5 py-4 pb-8">
          {activeTab === 'overview' && <DetailOverviewTab farmer={farmer} />}
          {activeTab === 'production' && <DetailProductionTab farmer={farmer} onNavigateTab={setActiveTab} />}
          {activeTab === 'diary' && <DetailDiaryTab farmer={farmer} onNavigateTab={setActiveTab} />}
          {activeTab === 'harvest' && <DetailHarvestTab farmer={farmer} />}
          {activeTab === 'ekycRisk' && <DetailEkycRiskTab farmer={farmer} />}
          {activeTab === 'finance' && <DetailFinanceTab farmer={farmer} />}
        </div>
      </div>
    </div>
  );
}
